import {radii, colors, VisuallyHidden} from '@commercial-helios/ui'
import {tokens} from '@pleo-io/telescope'
import React from 'react'
import styled, {css} from 'styled-components'
import {v4 as uuidv4} from 'uuid'

import Dropdown from '@helios/shared/components/dropdown'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

type InputProps = Omit<Partial<JSX.IntrinsicElements['input']>, 'css' | 'onChange' | 'ref'>

type Option = InputProps & {
    value: string
    label: string
}

export type PillsProps = {
    value: string
    options: Option[]
    onChange: (value: string) => void
    legend: string
}

export const Pills = ({options, value, onChange, legend}: PillsProps) => {
    const isSmallScreen = useMediaQuery.max('sm')
    const radioGroupName = uuidv4()

    return (
        <>
            <Fieldset>
                <legend>
                    <VisuallyHidden>{legend}</VisuallyHidden>
                </legend>

                {isSmallScreen ? (
                    <Dropdown options={options} value={value} onChange={onChange} />
                ) : (
                    <OptionsGrid>
                        {options.map((option) => (
                            <RadioOption
                                key={option.value}
                                name={radioGroupName}
                                value={option.value}
                                label={option.label}
                                checked={value === option.value}
                                onChange={onChange}
                            />
                        ))}
                    </OptionsGrid>
                )}
            </Fieldset>
        </>
    )
}

type RadioOptionProps = Option & Pick<PillsProps, 'onChange'>

const RadioOption = ({value, label, name, onChange, ...props}: RadioOptionProps) => {
    const id = uuidv4()

    return (
        <>
            <VisuallyHidden
                as="input"
                id={id}
                type="radio"
                name={name}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                children={null}
                {...props}
            />

            <StyledRadio htmlFor={id} $checked={props.checked}>
                {label}
            </StyledRadio>
        </>
    )
}

const gapY = tokens.spacing4
const gapX = tokens.spacing8

const OptionsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: -${gapY} -${gapX};
`

const activeStyles = css`
    color: ${colors.text.default};
    background: ${tokens.shade300};
`

type StyledRadioProps = {
    $checked?: boolean
}

const Fieldset = styled.fieldset`
    border: none;
`

const StyledRadio = styled.label<StyledRadioProps>`
    ${text({color: 'muted', variant: 'paragraph-base'})}
    display: inline-block;
    padding: ${tokens.spacing4} ${tokens.spacing12};
    margin: ${gapY} ${gapX};
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: ${radii.arc4};
    transition: ${tokens.fastIn};
    transition-property: background, color;

    :hover {
        ${activeStyles}
    }

    .focus-visible + & {
        ${focusStyles()}
    }

    ${(props) => props.$checked && activeStyles}
`
